import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/scrapSaleAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchCostCenters = createAsyncThunk(
    'scrapSaleCreation/fetchCostCenters',
    async ({ uid, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getScrapCostCenterCodes(uid, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost centers'));
        }
    }
);

export const fetchCategories = createAsyncThunk(
    'scrapSaleCreation/fetchCategories',
    async (_, { rejectWithValue }) => {
        try {
            const res = await api.getScrapCategories();
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load categories'));
        }
    }
);

export const fetchItemCodes = createAsyncThunk(
    'scrapSaleCreation/fetchItemCodes',
    async ({ cat, cc }, { rejectWithValue }) => {
        try {
            const res = await api.getScrapItemCodes(cat, cc);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load item codes'));
        }
    }
);

export const fetchGrid = createAsyncThunk(
    'scrapSaleCreation/fetchGrid',
    async (cc, { rejectWithValue }) => {
        try {
            const res = await api.viewScrapDetails(cc);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load scrap items'));
        }
    }
);

export const fetchClients = createAsyncThunk(
    'scrapSaleCreation/fetchClients',
    async (cc, { rejectWithValue }) => {
        try {
            const res = await api.getClientByCostCenter(null, cc);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load clients'));
        }
    }
);

export const fetchSubClients = createAsyncThunk(
    'scrapSaleCreation/fetchSubClients',
    async ({ cc, clientcode }, { rejectWithValue }) => {
        try {
            const res = await api.getSubClientsByClient(null, cc, clientcode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load sub-clients'));
        }
    }
);

export const fetchSaleMinDate = createAsyncThunk(
    'scrapSaleCreation/fetchSaleMinDate',
    async (cc, { rejectWithValue }) => {
        try {
            const res = await api.getScrapSaleDate(cc);
            const data = res?.Data;
            const record = Array.isArray(data) ? data[0] : data;
            return record?.Date || '';
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load minimum sale date'));
        }
    }
);

// Success format is "Submited" — anything else is an error message shown verbatim.
export const addItem = createAsyncThunk(
    'scrapSaleCreation/addItem',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveScrapItems(payload);
            const raw = res?.Data || '';
            if (raw.split(',')[0]?.trim() !== 'Submited') {
                return rejectWithValue(raw || 'Failed to add item');
            }
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to add item'));
        }
    }
);

export const deleteItem = createAsyncThunk(
    'scrapSaleCreation/deleteItem',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.deleteScrapItem(payload);
            const status = res?.Data || '';
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to delete item');
            }
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to delete item'));
        }
    }
);

// Success format is "Submited,<ScrapCode>".
export const submitScrapSale = createAsyncThunk(
    'scrapSaleCreation/submit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveScrapSale(payload);
            const raw = res?.Data || '';
            const [status, code] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to submit scrap sale');
            }
            return { code };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit scrap sale'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    categoryList: [],
    itemCodeList: [],
    gridItems: [],
    clientList: [],
    subClientList: [],
    saleMinDate: '',
    addResult: null,
    submitResult: null,
    loading: {
        ccList: false,
        categoryList: false,
        itemCodeList: false,
        grid: false,
        clients: false,
        subClients: false,
        saleMinDate: false,
        add: false,
        deleteItem: false,
        submit: false,
    },
    errors: {
        ccList: null,
        categoryList: null,
        itemCodeList: null,
        grid: null,
        clients: null,
        subClients: null,
        add: null,
        submit: null,
    },
};

const scrapSaleSlice = createSlice({
    name: 'scrapSaleCreation',
    initialState,
    reducers: {
        clearItemCodes:   (s) => { s.itemCodeList = []; },
        clearGrid:        (s) => { s.gridItems = []; s.errors.grid = null; },
        clearClients:     (s) => { s.clientList = []; s.subClientList = []; },
        clearSubClients:  (s) => { s.subClientList = []; },
        clearAddResult:   (s) => { s.addResult = null; s.errors.add = null; },
        clearSubmitResult:(s) => { s.submitResult = null; s.errors.submit = null; },
        resetScrapSale: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCostCenters.pending,   (s) => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchCostCenters.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchCostCenters.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchCategories.pending,   (s) => { s.loading.categoryList = true; s.errors.categoryList = null; })
            .addCase(fetchCategories.fulfilled, (s, a) => { s.loading.categoryList = false; s.categoryList = a.payload; })
            .addCase(fetchCategories.rejected,  (s, a) => { s.loading.categoryList = false; s.errors.categoryList = a.payload; });

        builder
            .addCase(fetchItemCodes.pending,   (s) => { s.loading.itemCodeList = true; s.errors.itemCodeList = null; })
            .addCase(fetchItemCodes.fulfilled, (s, a) => { s.loading.itemCodeList = false; s.itemCodeList = a.payload; })
            .addCase(fetchItemCodes.rejected,  (s, a) => { s.loading.itemCodeList = false; s.errors.itemCodeList = a.payload; });

        builder
            .addCase(fetchGrid.pending,   (s) => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridItems = a.payload; })
            .addCase(fetchGrid.rejected,  (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; s.gridItems = []; });

        builder
            .addCase(fetchClients.pending,   (s) => { s.loading.clients = true; s.errors.clients = null; })
            .addCase(fetchClients.fulfilled, (s, a) => { s.loading.clients = false; s.clientList = a.payload; })
            .addCase(fetchClients.rejected,  (s, a) => { s.loading.clients = false; s.errors.clients = a.payload; });

        builder
            .addCase(fetchSubClients.pending,   (s) => { s.loading.subClients = true; s.errors.subClients = null; })
            .addCase(fetchSubClients.fulfilled, (s, a) => { s.loading.subClients = false; s.subClientList = a.payload; })
            .addCase(fetchSubClients.rejected,  (s, a) => { s.loading.subClients = false; s.errors.subClients = a.payload; });

        builder
            .addCase(fetchSaleMinDate.pending,   (s) => { s.loading.saleMinDate = true; })
            .addCase(fetchSaleMinDate.fulfilled, (s, a) => { s.loading.saleMinDate = false; s.saleMinDate = a.payload; })
            .addCase(fetchSaleMinDate.rejected,  (s) => { s.loading.saleMinDate = false; s.saleMinDate = ''; });

        builder
            .addCase(addItem.pending,   (s) => { s.loading.add = true; s.errors.add = null; s.addResult = null; })
            .addCase(addItem.fulfilled, (s) => { s.loading.add = false; s.addResult = true; })
            .addCase(addItem.rejected,  (s, a) => { s.loading.add = false; s.errors.add = a.payload; });

        builder
            .addCase(deleteItem.pending,   (s) => { s.loading.deleteItem = true; })
            .addCase(deleteItem.fulfilled, (s) => { s.loading.deleteItem = false; })
            .addCase(deleteItem.rejected,  (s) => { s.loading.deleteItem = false; });

        builder
            .addCase(submitScrapSale.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitScrapSale.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; s.gridItems = []; })
            .addCase(submitScrapSale.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearItemCodes,
    clearGrid,
    clearClients,
    clearSubClients,
    clearAddResult,
    clearSubmitResult,
    resetScrapSale,
} = scrapSaleSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectSSCCList        = (s) => s.scrapSaleCreation.ccList;
export const selectSSCategoryList  = (s) => s.scrapSaleCreation.categoryList;
export const selectSSItemCodeList  = (s) => s.scrapSaleCreation.itemCodeList;
export const selectSSGridItems     = (s) => s.scrapSaleCreation.gridItems;
export const selectSSClientList    = (s) => s.scrapSaleCreation.clientList;
export const selectSSSubClientList = (s) => s.scrapSaleCreation.subClientList;
export const selectSSSaleMinDate   = (s) => s.scrapSaleCreation.saleMinDate;
export const selectSSAddResult     = (s) => s.scrapSaleCreation.addResult;
export const selectSSSubmitResult  = (s) => s.scrapSaleCreation.submitResult;
export const selectSSLoading       = (s) => s.scrapSaleCreation.loading;
export const selectSSErrors        = (s) => s.scrapSaleCreation.errors;

export default scrapSaleSlice.reducer;
